/**
 * PECE Progress Claims Queue — Sprint 3 Step 3.4 (Frontend)
 *
 * Closes the loop: PPR → MAR → MIR → Delivery → Consumption → CLAIM.
 *
 * Backend endpoints:
 *   GET  /api/pece/claims                  (filter project_id/status/claim_type)
 *   GET  /api/pece/claims/{id}
 *   POST /api/pece/claims                  (manual create)
 *   POST /api/pece/claims/{id}/submit
 *   POST /api/pece/claims/{id}/certify
 *   POST /api/pece/claims/{id}/approve
 *   POST /api/pece/claims/{id}/reject
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  FileCheck2, RefreshCw, Loader2, ListChecks, Activity, FileText,
  Check, X, AlertTriangle, ChevronDown, ChevronUp, Send, Stamp,
  CheckCircle2, Banknote, Hash, Building2, Sparkles,
} from 'lucide-react';

const STATUS_META = {
  draft:     { label: 'مسودة',     cls: 'bg-gray-100 text-gray-700 border-gray-300',     step: 1 },
  submitted: { label: 'مُقدَّم',     cls: 'bg-blue-50 text-blue-700 border-blue-300',     step: 2 },
  certified: { label: 'مُصدَّق',     cls: 'bg-indigo-50 text-indigo-700 border-indigo-300', step: 3 },
  approved:  { label: 'مُعتمَد',     cls: 'bg-emerald-50 text-emerald-700 border-emerald-300', step: 4 },
  rejected:  { label: 'مرفوض',     cls: 'bg-rose-50 text-rose-700 border-rose-300',     step: -1 },
};

const TYPE_META = {
  progress:      { label: 'دفعة تنفيذ',   cls: 'bg-violet-50 text-violet-700 border-violet-200' },
  material_only: { label: 'مواد فقط',     cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  variation:     { label: 'أمر تغيير',     cls: 'bg-cyan-50 text-cyan-700 border-cyan-200' },
};

const fmtMoney = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const PECEClaimsQueue = () => {
  const [tab, setTab] = useState('pending');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingOn, setActingOn] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [rejectModal, setRejectModal] = useState(null);
  const [createModal, setCreateModal] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const r = await axiosInstance.get('/pece/claims?limit=300');
        if (cancelled) return;
        let data = r.data || [];
        if (tab === 'pending') {
          data = data.filter((c) => ['draft', 'submitted', 'certified'].includes(c.status));
        } else if (tab === 'approved') {
          data = data.filter((c) => c.status === 'approved');
        } else if (tab === 'rejected') {
          data = data.filter((c) => c.status === 'rejected');
        }
        setRows(data);
      } catch (e) {
        if (!cancelled) toast.error('فشل تحميل قائمة المطالبات');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [tab, reloadTick]);

  const reload = () => setReloadTick((t) => t + 1);

  const act = async (claim, endpoint, body, successMsg) => {
    setActingOn(claim.id);
    try {
      const r = await axiosInstance.post(`/pece/claims/${claim.id}/${endpoint}`, body || {});
      toast.success(`${successMsg} · ${r.data.claim_no}`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل الإجراء');
    } finally {
      setActingOn(null);
    }
  };

  const reject = async (claim, reason) => {
    setActingOn(claim.id);
    try {
      await axiosInstance.post(`/pece/claims/${claim.id}/reject`, { reason });
      toast.success(`تم رفض ${claim.claim_no}`);
      setRejectModal(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل الرفض');
    } finally {
      setActingOn(null);
    }
  };

  const totals = rows.reduce((acc, c) => {
    acc.count += 1;
    acc.value += Number(c.total_claimed_value || 0);
    if (c.source === 'auto_from_consumption') acc.auto += 1;
    return acc;
  }, { count: 0, value: 0, auto: 0 });

  return (
    <div className="min-h-screen bg-gray-50" data-testid="pece-claims-page">
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs text-violet-600 font-bold uppercase tracking-wider mb-1 flex items-center gap-2">
                <FileCheck2 className="w-4 h-4" /> PECE · Progress Claims
              </div>
              <h1 className="text-2xl font-bold text-gray-900">مطالبات تنفيذ المشاريع (Progress Claims)</h1>
              <p className="text-sm text-gray-500 mt-1">
                مطالبات مبنية على الاستهلاك الفعلي بالموقع — كل سطر يحمل لقطة (snapshot) ثابتة لسعر الوحدة والقيمة وقت الاستهلاك.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                data-testid="claims-create-btn"
                onClick={() => setCreateModal(true)}
                className="px-4 py-2.5 bg-violet-600 text-white rounded-lg hover:bg-violet-700 flex items-center gap-2 font-semibold text-sm"
              >
                <Sparkles className="w-4 h-4" /> مطالبة يدوية
              </button>
              <button
                data-testid="claims-refresh-btn"
                onClick={reload}
                className="px-4 py-2.5 border border-violet-300 text-violet-700 rounded-lg hover:bg-violet-50 flex items-center gap-2 font-semibold text-sm"
              >
                <RefreshCw className="w-4 h-4" /> تحديث
              </button>
            </div>
          </div>

          <div className="flex gap-1 mt-5 flex-wrap">
            <TabBtn id="pending" active={tab} onClick={setTab} icon={<ListChecks className="w-4 h-4" />}>
              قيد المراجعة <span className="ml-2 bg-violet-100 text-violet-700 text-xs px-2 py-0.5 rounded-full">{tab === 'pending' ? rows.length : '·'}</span>
            </TabBtn>
            <TabBtn id="approved" active={tab} onClick={setTab} icon={<CheckCircle2 className="w-4 h-4" />}>
              معتمدة
            </TabBtn>
            <TabBtn id="rejected" active={tab} onClick={setTab} icon={<X className="w-4 h-4" />}>
              مرفوضة
            </TabBtn>
            <TabBtn id="all" active={tab} onClick={setTab} icon={<Activity className="w-4 h-4" />}>
              الكل
            </TabBtn>
          </div>
        </div>
      </div>

      {/* KPI strip */}
      <div className="max-w-7xl mx-auto px-6 pt-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="claims-kpi-strip">
          <KPI icon={<Hash className="w-5 h-5" />} label="عدد المطالبات" value={totals.count} accent="violet" />
          <KPI icon={<Banknote className="w-5 h-5" />} label="إجمالي القيمة (Snapshot)" value={fmtMoney(totals.value)} accent="emerald" />
          <KPI icon={<Sparkles className="w-5 h-5" />} label="مطالبات تلقائية" value={`${totals.auto} / ${totals.count || 0}`} accent="amber" />
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {loading && (
          <div className="flex items-center justify-center py-20 text-gray-500" data-testid="claims-loading">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> جاري التحميل...
          </div>
        )}

        {!loading && rows.length === 0 && (
          <div className="bg-white rounded-xl border border-dashed border-gray-300 p-12 text-center" data-testid="claims-empty">
            <FileCheck2 className="w-12 h-12 mx-auto text-violet-500 mb-3" />
            <h3 className="text-lg font-bold text-gray-800">لا توجد مطالبات</h3>
            <p className="text-sm text-gray-500 mt-1">
              {tab === 'pending'
                ? 'لا توجد مطالبات قيد المراجعة. يتم توليدها تلقائياً عند اكتمال استهلاك حجوزات المواد بالموقع.'
                : 'لم يتم إنشاء أي مطالبة في هذا التصنيف بعد.'}
            </p>
          </div>
        )}

        {!loading && rows.length > 0 && (
          <div className="space-y-3" data-testid="claims-list">
            {rows.map((c) => (
              <ClaimCard
                key={c.id}
                claim={c}
                expanded={expandedId === c.id}
                isActing={actingOn === c.id}
                onToggle={() => setExpandedId(expandedId === c.id ? null : c.id)}
                onSubmit={() => act(c, 'submit', {}, 'تم تقديم المطالبة')}
                onCertify={() => act(c, 'certify', {}, 'تم تصديق المطالبة')}
                onApprove={() => act(c, 'approve', {}, 'تم اعتماد المطالبة')}
                onReject={() => setRejectModal(c)}
              />
            ))}
          </div>
        )}
      </div>

      {rejectModal && (
        <RejectModal
          claim={rejectModal}
          onCancel={() => setRejectModal(null)}
          onConfirm={(reason) => reject(rejectModal, reason)}
          isActing={actingOn === rejectModal.id}
        />
      )}

      {createModal && (
        <CreateClaimModal
          onCancel={() => setCreateModal(false)}
          onCreated={() => { setCreateModal(false); reload(); }}
        />
      )}
    </div>
  );
};

const TabBtn = ({ id, active, onClick, children, icon }) => (
  <button
    data-testid={`claims-tab-${id}`}
    onClick={() => onClick(id)}
    className={`px-4 py-2.5 text-sm font-medium border-b-2 transition flex items-center gap-2 ${
      active === id
        ? 'border-violet-600 text-violet-700'
        : 'border-transparent text-gray-600 hover:text-gray-900'
    }`}
  >
    {icon}{children}
  </button>
);

const KPI = ({ icon, label, value, accent }) => (
  <div className={`bg-white border border-${accent}-200 rounded-xl p-4 flex items-center gap-3`}>
    <div className={`w-10 h-10 rounded-lg bg-${accent}-50 text-${accent}-600 flex items-center justify-center`}>{icon}</div>
    <div>
      <div className="text-xs text-gray-500 font-semibold">{label}</div>
      <div className="text-xl font-bold text-gray-900 mt-0.5">{value}</div>
    </div>
  </div>
);

const StepBadge = ({ n, label, active, done, rejected }) => (
  <div className={`flex flex-col items-center gap-1 ${rejected ? 'opacity-40' : ''}`}>
    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold border-2 ${
      done ? 'bg-emerald-500 text-white border-emerald-500' :
      active ? 'bg-violet-500 text-white border-violet-500 ring-2 ring-violet-200' :
      'bg-white text-gray-400 border-gray-300'
    }`}>
      {done ? '✓' : n}
    </div>
    <div className="text-[9px] text-gray-600 font-medium">{label}</div>
  </div>
);

const ClaimCard = ({ claim, expanded, isActing, onToggle,
                    onSubmit, onCertify, onApprove, onReject }) => {
  const meta = STATUS_META[claim.status] || STATUS_META.draft;
  const typeMeta = TYPE_META[claim.claim_type] || TYPE_META.progress;
  const step = meta.step;
  const canSubmit  = claim.status === 'draft';
  const canCertify = claim.status === 'submitted';
  const canApprove = claim.status === 'certified';
  const canReject  = ['submitted', 'certified'].includes(claim.status);
  const isRejected = claim.status === 'rejected';
  const isAuto = claim.source === 'auto_from_consumption';

  return (
    <div
      data-testid={`claim-card-${claim.id}`}
      className="bg-white rounded-xl border border-gray-200 hover:shadow-sm transition"
    >
      <div className="p-5">
        <div className="flex items-start gap-4 flex-wrap">
          {/* Status pill */}
          <div className={`px-3 py-2 rounded-lg border ${meta.cls} text-center min-w-[140px]`}>
            <FileCheck2 className="w-5 h-5 mx-auto" />
            <div className="text-[10px] uppercase font-bold tracking-wider mt-1">{meta.label}</div>
          </div>

          <div className="flex-1 min-w-[260px]">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono font-bold text-base text-gray-900">{claim.claim_no}</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${typeMeta.cls}`}>{typeMeta.label}</span>
                  {isAuto && (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold border bg-amber-50 text-amber-700 border-amber-200 flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> توليد تلقائي
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Project: <span className="font-mono font-semibold text-gray-700">{claim.project_id?.slice(0, 8)}…</span>
                  {claim.is_demo && <span className="ml-2 text-[10px] bg-pink-50 text-pink-600 px-1.5 py-0.5 rounded">DEMO</span>}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] text-gray-500 font-semibold uppercase">إجمالي المطالبة</div>
                <div className="text-2xl font-bold text-emerald-700 font-mono" data-testid={`claim-total-${claim.id}`}>
                  {fmtMoney(claim.total_claimed_value)}
                </div>
                <div className="text-[10px] text-gray-500">{claim.lines?.length || 0} سطر</div>
              </div>
            </div>

            {/* Step ladder */}
            <div className="mt-3 flex items-center gap-4">
              <StepBadge n={1} label="مسودة"    active={step === 1} done={step > 1}             rejected={isRejected} />
              <div className="flex-1 h-0.5 bg-gray-200" />
              <StepBadge n={2} label="مُقدَّم"    active={step === 2} done={step > 2}             rejected={isRejected} />
              <div className="flex-1 h-0.5 bg-gray-200" />
              <StepBadge n={3} label="مُصدَّق"    active={step === 3} done={step > 3}             rejected={isRejected} />
              <div className="flex-1 h-0.5 bg-gray-200" />
              <StepBadge n={4} label="مُعتمَد"    active={step === 4} done={step === 4}            rejected={isRejected} />
            </div>

            <div className="mt-3 flex items-center gap-2 flex-wrap">
              {canSubmit && (
                <ActionBtn data-testid={`claim-submit-${claim.id}`} disabled={isActing || !claim.lines?.length} onClick={onSubmit} color="blue" icon={<Send className="w-3.5 h-3.5" />}>
                  تقديم
                </ActionBtn>
              )}
              {canCertify && (
                <ActionBtn data-testid={`claim-certify-${claim.id}`} disabled={isActing} onClick={onCertify} color="indigo" icon={<Stamp className="w-3.5 h-3.5" />}>
                  تصديق (استشاري)
                </ActionBtn>
              )}
              {canApprove && (
                <ActionBtn data-testid={`claim-approve-${claim.id}`} disabled={isActing} onClick={onApprove} color="emerald" icon={<Check className="w-3.5 h-3.5" />}>
                  اعتماد نهائي
                </ActionBtn>
              )}
              {canReject && (
                <ActionBtn data-testid={`claim-reject-${claim.id}`} disabled={isActing} onClick={onReject} color="rose" icon={<X className="w-3.5 h-3.5" />}>
                  رفض
                </ActionBtn>
              )}
              {canSubmit && !claim.lines?.length && (
                <span className="text-[11px] text-amber-700 bg-amber-50 px-2 py-1 rounded border border-amber-200 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> لا يمكن التقديم — لا توجد سطور
                </span>
              )}
              {claim.correlation_id && (
                <Link
                  data-testid={`claim-ecp-link-${claim.id}`}
                  to={`/ecp?correlation_id=${claim.correlation_id}`}
                  className="text-[11px] text-violet-700 hover:underline ml-auto flex items-center gap-1"
                >
                  <Activity className="w-3 h-3" /> ECP Chain
                </Link>
              )}
              <button
                data-testid={`claim-expand-${claim.id}`}
                onClick={onToggle}
                className="text-[11px] text-gray-600 hover:text-gray-900 flex items-center gap-1"
              >
                {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                {expanded ? 'إخفاء التفاصيل' : 'عرض التفاصيل'}
              </button>
            </div>
          </div>
        </div>

        {expanded && (
          <div className="mt-4 pt-4 border-t border-gray-200 space-y-3" data-testid={`claim-detail-${claim.id}`}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <Detail icon={<FileText />} label="إنشاء" value={fmtDate(claim.created_at)} />
              <Detail icon={<Send />} label="تقديم" value={fmtDate(claim.submitted_at)} />
              <Detail icon={<Stamp />} label="تصديق" value={fmtDate(claim.certified_at)} />
              <Detail icon={<CheckCircle2 />} label="اعتماد" value={fmtDate(claim.approved_at)} />
            </div>

            {claim.rejection_reason && (
              <div className="text-xs bg-rose-50 border border-rose-200 text-rose-700 px-3 py-2 rounded">
                <span className="font-bold">سبب الرفض:</span> {claim.rejection_reason}
              </div>
            )}

            <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-3 py-2 text-[11px] font-bold text-gray-700 uppercase border-b border-gray-200 bg-white flex items-center gap-2">
                <FileText className="w-3.5 h-3.5" /> سطور المطالبة (Snapshot ثابت)
              </div>
              {!claim.lines?.length ? (
                <div className="px-3 py-4 text-xs text-gray-500 text-center">لا توجد سطور بعد. ستُضاف تلقائياً عند اكتمال استهلاك الحجوزات.</div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="text-[10px] uppercase text-gray-500 bg-white border-b">
                    <tr>
                      <th className="text-right px-3 py-2">المادة</th>
                      <th className="text-right px-3 py-2">Activity</th>
                      <th className="text-right px-3 py-2">كمية مستهلكة</th>
                      <th className="text-right px-3 py-2">سعر الوحدة</th>
                      <th className="text-right px-3 py-2">القيمة</th>
                      <th className="text-right px-3 py-2">المصدر</th>
                    </tr>
                  </thead>
                  <tbody>
                    {claim.lines.map((l) => (
                      <tr key={l.line_id} className="border-t border-gray-100" data-testid={`claim-line-${l.line_id}`}>
                        <td className="px-3 py-2 font-semibold text-gray-800">{l.material_name || '—'}</td>
                        <td className="px-3 py-2 font-mono text-[11px] text-gray-600">{l.activity_code || '—'}</td>
                        <td className="px-3 py-2 font-mono">{fmtMoney(l.consumed_qty)}</td>
                        <td className="px-3 py-2 font-mono text-gray-700">{fmtMoney(l.unit_rate)}</td>
                        <td className="px-3 py-2 font-mono font-bold text-emerald-700">{fmtMoney(l.claimed_value)}</td>
                        <td className="px-3 py-2 text-[10px] text-gray-500">{l.source === 'auto_from_consumption' ? 'تلقائي' : 'يدوي'}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-white border-t-2 border-gray-300">
                    <tr>
                      <td colSpan={4} className="px-3 py-2 text-right font-bold text-gray-700">الإجمالي:</td>
                      <td className="px-3 py-2 font-mono font-bold text-emerald-700">{fmtMoney(claim.total_claimed_value)}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ActionBtn = ({ children, disabled, onClick, color, icon, ...rest }) => {
  const colorMap = {
    blue: 'bg-blue-600 hover:bg-blue-700',
    indigo: 'bg-indigo-600 hover:bg-indigo-700',
    emerald: 'bg-emerald-600 hover:bg-emerald-700',
    rose: 'bg-rose-600 hover:bg-rose-700',
  };
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      className={`${colorMap[color]} disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-bold px-3 py-1.5 rounded-md flex items-center gap-1.5`}
      {...rest}
    >
      {icon}{children}
    </button>
  );
};

const Detail = ({ icon, label, value }) => (
  <div className="flex items-start gap-2">
    <div className="w-6 h-6 rounded bg-gray-100 text-gray-500 flex items-center justify-center flex-shrink-0">
      {React.cloneElement(icon, { className: 'w-3 h-3' })}
    </div>
    <div className="min-w-0">
      <div className="text-[10px] font-semibold text-gray-500 uppercase">{label}</div>
      <div className="text-[11px] text-gray-800 truncate">{value}</div>
    </div>
  </div>
);

const RejectModal = ({ claim, onCancel, onConfirm, isActing }) => {
  const [reason, setReason] = useState('');
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="claim-reject-modal">
      <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 rounded-lg bg-rose-100 text-rose-600 flex items-center justify-center">
            <X className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900">رفض المطالبة {claim.claim_no}</h3>
            <p className="text-xs text-gray-500">يرجى ذكر سبب الرفض — سيتم تسجيله في سجل التدقيق.</p>
          </div>
        </div>
        <textarea
          data-testid="claim-reject-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="السبب (مثل: خلاف على الأسعار، أخطاء في الكميات...)"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm h-24 resize-none focus:ring-2 focus:ring-rose-200 outline-none"
        />
        <div className="flex justify-end gap-2 mt-4">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            إلغاء
          </button>
          <button
            data-testid="claim-reject-confirm"
            disabled={isActing || !reason.trim()}
            onClick={() => onConfirm(reason.trim())}
            className="px-4 py-2 text-sm bg-rose-600 text-white rounded-lg hover:bg-rose-700 disabled:opacity-50 flex items-center gap-2"
          >
            {isActing && <Loader2 className="w-4 h-4 animate-spin" />}
            تأكيد الرفض
          </button>
        </div>
      </div>
    </div>
  );
};

const CreateClaimModal = ({ onCancel, onCreated }) => {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [claimType, setClaimType] = useState('progress');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axiosInstance.get('/projects').then((r) => setProjects(r.data || [])).catch(() => {});
  }, []);

  const submit = async () => {
    if (!projectId) {
      toast.error('الرجاء اختيار مشروع');
      return;
    }
    setBusy(true);
    try {
      const r = await axiosInstance.post('/pece/claims', {
        project_id: projectId,
        claim_type: claimType,
        notes: notes || null,
      });
      toast.success(`تم إنشاء ${r.data.claim_no}`);
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل إنشاء المطالبة');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="claim-create-modal">
      <div className="bg-white rounded-xl max-w-lg w-full p-6 shadow-2xl">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-9 h-9 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center">
            <FileCheck2 className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900">إنشاء مطالبة يدوية</h3>
            <p className="text-xs text-gray-500">ستبدأ بحالة <b>مسودة</b> بدون سطور — أضف السطور لاحقاً قبل التقديم.</p>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold text-gray-700 uppercase mb-1 block">المشروع</label>
            <select
              data-testid="claim-create-project"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">— اختر مشروع —</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-gray-700 uppercase mb-1 block">نوع المطالبة</label>
            <select
              data-testid="claim-create-type"
              value={claimType}
              onChange={(e) => setClaimType(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="progress">دفعة تنفيذ (Progress)</option>
              <option value="material_only">مواد فقط (Material Only)</option>
              <option value="variation">أمر تغيير (Variation)</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-gray-700 uppercase mb-1 block">ملاحظات (اختياري)</label>
            <textarea
              data-testid="claim-create-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm h-20 resize-none"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            إلغاء
          </button>
          <button
            data-testid="claim-create-confirm"
            disabled={busy || !projectId}
            onClick={submit}
            className="px-4 py-2 text-sm bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50 flex items-center gap-2"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            إنشاء
          </button>
        </div>
      </div>
    </div>
  );
};

export default PECEClaimsQueue;
